/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { PageAction, PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { logger } from "@octopusdeploy/logging";
import { Permission, canCommitTo, HasVariablesInGit, isGitBranchResource, TenantedDeploymentMode, VariableType } from "@octopusdeploy/octopus-server-client";
import type { GitBranchResource, GitRefResource, ProjectResource, VariableResource, VariableSetResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { Dictionary } from "lodash";
import * as React from "react";
import { useLocation } from "react-router-dom";
import type { ActionEvent, AnalyticActionDispatcher, AnalyticTrackedActionDispatcher } from "~/analytics/Analytics";
import { Action, useProjectScopedAnalyticActionDispatch, useProjectScopedAnalyticTrackedActionDispatch } from "~/analytics/Analytics";
import { ProjectGitHubAppAuthCheck } from "~/areas/library/components/GitConnections/GitHubAppAuthCheck";
import { PageHeaderBranchSelector } from "~/areas/projects/components/BranchSelector/PageHeaderBranchSelector";
import { SampleProjectTourContext } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import type { SampleProjectTourContextProps } from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import NoProjectVariablesOnboardingPage from "~/areas/projects/components/Variables/ProjectVariables/NoProjectVariablesOnboardingPage";
import { GitCallouts } from "~/areas/projects/components/VersionControl/GitCallouts";
import type { ProjectContextProps } from "~/areas/projects/context";
import { useProjectContext } from "~/areas/projects/context";
import VariableEditor from "~/areas/variables/VariableEditor/VariableEditor";
import { default as VariableSaveConfirmationDialog } from "~/areas/variables/VariableSaveConfirmationDialog/VariableSaveConfirmationDialog";
import { repository } from "~/clientInstance";
import type { PrimarySaveActionOptions, PrimarySavePageAction } from "~/components/FormPaperLayout/LegacyForm";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { useRequiredContext } from "~/hooks/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import DateFormatter from "~/utils/DateFormatter/DateFormatter";
import IdHelper from "~/utils/IdHelper/index";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import { OptionalFormBaseComponent } from "../../../../../components/FormBaseComponent/FormBaseComponent";
import type { OptionalFormBaseComponentState } from "../../../../../components/FormBaseComponent/FormBaseComponent";
import type { ReadonlyVariableResource } from "../../../../variables/ReadonlyVariableResource";
import { createDialogContent, createViewModel, getVariableResources } from "../../../../variables/VariableEditor/conversions";
import type { VariableSaveConfirmationContent } from "../../../../variables/VariableSaveConfirmationDialog/VariableSaveConfirmationDialog";
import type { VariableModel } from "../../../../variables/VariablesModel/VariablesModel";
import groupVariablesByName from "../../../../variables/groupVariablesByName";
import { useEphemeralEnvironmentsEnabled } from "../../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import type { GetCommitButtonProps } from "../../VersionControl/CommitButton";
import { GetCommitButton } from "../../VersionControl/CommitButton";
import type { CommitMessageWithDetails } from "../../VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "../../VersionControl/CommitMessageWithDetails";
import { VcsError } from "../../VersionControl/VcsError";
import MigrateProjectVariablesBanner from "./MigrateProjectVariablesToGit/MigrateProjectVariablesBanner";
import { getProjectVariablesPrimaryAction, getVariableTypesChanged } from "./projectVariableChanges";
interface SharedProjectVariablesPageInternalState extends OptionalFormBaseComponentState<SharedProjectVariablesPageInternalModel> {
    variableSet?: VariableSetResource;
    sensitiveVariableSet?: VariableSetResource;
    project?: ProjectResource;
    model?: SharedProjectVariablesPageInternalModel;
    dialogContent?: VariableSaveConfirmationContent;
    newBranch?: GitBranchResource;
    initialVariables?: ReadonlyArray<VariableModel>;
    groupedVariableResources?: Dictionary<ReadonlyVariableResource[]>;
    cellFocusResetKey: string;
    disableDirtyFormChecking?: boolean;
    commitMessage: CommitMessageWithDetails;
    hasSteps: boolean;
    showOnboardingPage: boolean;
    advancedChangeDetection: AdvancedChangeDetection;
}
interface SharedProjectVariablesPageInternalModel {
    readonly variables: ReadonlyArray<VariableModel>;
}
type SharedProjectVariablesPageInternalProps = {
    projectContext: ProjectContextProps;
    trackAction: AnalyticTrackedActionDispatcher;
    onboardingContext?: SampleProjectTourContextProps;
    dispatchAction: AnalyticActionDispatcher;
    ephemeralEnvironmentsAreEnabled: boolean;
    gitVariableSplitChangeDetectionEnabled: boolean;
    navigateToGitRef: (gitRef: string) => void;
};
type AdvancedChangeDetection = AdvancedChangeDetectionEnabled | AdvancedChangeDetectionDisabled;
type AdvancedChangeDetectionDisabled = {
    isEnabled: false;
};
type AdvancedChangeDetectionEnabled = {
    isEnabled: true;
    sensitiveChanged: boolean;
    textChanged: boolean;
};
const pageTitle = "Project Variables";
class SharedProjectVariablesPageInternal extends OptionalFormBaseComponent<SharedProjectVariablesPageInternalProps, SharedProjectVariablesPageInternalState, SharedProjectVariablesPageInternalModel> {
    private openCommitDialog?: () => void;
    constructor(props: SharedProjectVariablesPageInternalProps) {
        super(props);
        this.state = {
            cellFocusResetKey: DateFormatter.timestamp(),
            commitMessage: { summary: "", details: "" },
            hasSteps: false,
            showOnboardingPage: true,
            advancedChangeDetection: { isEnabled: false },
        };
    }
    async componentDidMount() {
        await this.reload();
    }
    async componentDidUpdate(prevProps: SharedProjectVariablesPageInternalProps) {
        if (prevProps.projectContext.state.gitRef !== this.props.projectContext.state.gitRef) {
            await this.reload();
        }
    }
    private reload = async () => await this.doBusyTask(async () => {
        const { model: project, projectContextRepository, gitRef } = this.props.projectContext.state;
        if (HasVariablesInGit(project.PersistenceSettings) && !gitRef) {
            // If we've just changed from an invalid branch, GitRef might be null. Wait until it's set before
            // we proceed with the reload or the call on the next line will fail.
            return;
        }
        const variableSet = await this.props.projectContext.state.projectContextRepository.Variables.get();
        let hasSteps = false;
        try {
            const deploymentProcess = await projectContextRepository.DeploymentProcesses.get();
            hasSteps = deploymentProcess.Steps.length > 0;
        }
        catch (exception) {
            logger.error(exception, "Failed to get deployment processes for {project}", { project });
        }
        if (HasVariablesInGit(project.PersistenceSettings)) {
            const sensitiveVariableSet = await this.props.projectContext.state.projectContextRepository.Variables.getSensitive();
            this.resetState(project, hasSteps, variableSet, sensitiveVariableSet);
        }
        else {
            this.resetState(project, hasSteps, variableSet);
        }
    }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    private getCommitButtonProps(primarySaveAction: PrimarySavePageAction, sensitiveChanged: boolean): GetCommitButtonProps {
        return {
            hasPermission: { permission: Permission.ProjectEdit, project: this.props.projectContext.state.model.Id },
            gitRef: this.props.projectContext.state.gitRef?.CanonicalName,
            persistenceSettings: this.props.projectContext.state.model.PersistenceSettings,
            canCommitToGitRef: canCommitTo(this.props.projectContext.state.gitRef),
            defaultCommitMessage: "Update variables",
            commitMessage: this.state.commitMessage,
            updateCommitMessage: (commitMessage: CommitMessageWithDetails) => this.setState({ commitMessage }),
            commitMessageAccessibleName: "Commit message for saving the variables",
            commitDetailsAccessibleName: "Commit details for saving the variables",
            commitButtonAccessibleName: "Commit changes to the variables",
            onNewBranchCreating: (branchName) => this.saveVariablesToNewBranch(branchName),
            onInitializing: (openCommitDialog: () => void) => (this.openCommitDialog = openCommitDialog),
            label: primarySaveAction.label,
            busyLabel: primarySaveAction.busyLabel,
            onClick: primarySaveAction.onClick,
            disabled: primarySaveAction.disabled,
            sensitiveValueChanged: sensitiveChanged,
        };
    }
    private getOverflowActions(project?: ProjectResource): SimpleMenuItem[] {
        if (!project) {
            return [];
        }
        if (HasVariablesInGit(project.PersistenceSettings)) {
            return [
                {
                    type: "download-link",
                    label: "Download Text as JSON",
                    href: this.props.projectContext.state.projectContextRepository.Variables.resolveResourceLink(),
                    downloadFileName: `${project.Slug}-text-variables.json`,
                },
                {
                    type: "download-link",
                    label: "Download Sensitive as JSON",
                    href: this.props.projectContext.state.projectContextRepository.Variables.resolveSensitiveResourceLink(),
                    downloadFileName: `${project.Slug}-sensitive-variables.json`,
                },
            ];
        }
        return [
            {
                type: "download-link",
                label: "Download as JSON",
                href: this.props.projectContext.state.projectContextRepository.Variables.resolveResourceLink(),
                downloadFileName: `${project.Slug}-variables.json`,
            },
        ];
    }
    render() {
        const overflowActions = this.getOverflowActions(this.state.project);
        const gitVariables = this.state.project ? HasVariablesInGit(this.state.project.PersistenceSettings) : false;
        const onSaveClick = async (isNavigationConfirmation?: boolean | undefined, onSavedCallback?: (() => void) | undefined, newBranch?: GitRefResource | undefined, commitMessage?: CommitMessageWithDetails | undefined) => {
            if (newBranch !== undefined && !isGitBranchResource(newBranch)) {
                return;
            }
            const newBranchResource: GitBranchResource | undefined = newBranch;
            if (isNavigationConfirmation && this.openCommitDialog && gitVariables) {
                this.openCommitDialog();
            }
            else {
                const dialogContent = createDialogContent(this.state.model!.variables, this.state.initialVariables!, this.state.variableSet!.Variables);
                if (dialogContent && dialogContent.hasContent) {
                    this.setState({ dialogContent, newBranch: newBranchResource });
                }
                else {
                    await this.doBusyTask(() => this.saveVariables(newBranchResource));
                    if (onSavedCallback) {
                        onSavedCallback();
                    }
                }
            }
        };
        const pageActions: PageAction[] = [];
        const { dispatchAction } = this.props;
        const createReleaseIsAllowed = this.state.hasSteps || this.state.project?.IsVersionControlled;
        if (this.state.project && createReleaseIsAllowed) {
            const createReleaseButton: PageAction = {
                type: "navigate",
                buttonType: "secondary",
                label: "Create Release",
                path: links.createReleasePage.generateUrl({ spaceId: this.state.project.SpaceId, projectSlug: this.state.project.Slug }),
                onClick: () => dispatchAction("Create a release", { resource: "Create Release", action: Action.Add }),
                hasPermissions: isAllowed({ permission: Permission.ReleaseCreate, project: this.state.project.Id, tenant: "*" }),
            };
            pageActions.push(createReleaseButton);
        }
        const showOnboarding = this.state.showOnboardingPage && this.state.initialVariables?.length === 0;
        if (showOnboarding) {
            return (<PageContent header={{
                    title: pageTitle,
                    contextSelector: this.state.project ? <PageHeaderBranchSelector project={this.state.project} gitRef={this.props.projectContext.state.gitRef?.CanonicalName} setGitRef={this.props.navigateToGitRef}/> : undefined,
                }} errors={this.errors} busy={this.state.busy}>
                    <NoProjectVariablesOnboardingPage onClick={() => {
                    this.setState({ showOnboardingPage: false });
                }} gitRefResource={this.props.projectContext.state.gitRef}/>
                </PageContent>);
        }
        // Dirty form checking on this page is unreliable as the variable editor
        // sometimes reorders variables in the model, so it might be different
        // to the clean model (even if nothing has changed). In May 2025, we
        // added support for correctly detecting if there are changes to the
        // sensitive or text variables independently so that we could change the
        // button text and Git saving behaviour. We can use this as a more
        // reliable way to detect if there have been _any_ changes.
        //
        // Legacy form only detects changes based on model vs cleanModel, and
        // doesn't give us a way to override the dirty state. To work around
        // this, we disable the dirty form checking and manually disable the
        // button if we're using the new change detection and we've detected
        // that there are _no_ changes. This stops the built-in dirty-state
        // checking enabling the save button incorrectly.
        //
        // We can't just disable the dirty form checking all the time, and
        // manually enable/disable the buttons otherwise we will lose the
        // navigate away check.
        const disableChecking = this.state.advancedChangeDetection.isEnabled && !this.state.advancedChangeDetection.sensitiveChanged && !this.state.advancedChangeDetection.textChanged;
        return (<LegacyForm model={this.state.model} cleanModel={this.state.cleanModel} savePermission={{
                permission: Permission.VariableEdit,
                project: this.state.project && this.state.project.Id,
                wildcard: true,
            }} onSaveClick={onSaveClick} disableDirtyFormChecking={this.state.disableDirtyFormChecking || disableChecking}>
                {({ FormContent, createSaveAction }) => (<PageContent busy={this.state.busy} errors={this.errors} header={{
                    title: pageTitle,
                    overflowActions,
                    primaryAction: this.getPrimaryAction(gitVariables, createSaveAction),
                    pageActions,
                    contextSelector: this.state.project && <PageHeaderBranchSelector project={this.state.project} gitRef={this.props.projectContext.state.gitRef?.CanonicalName} setGitRef={this.props.navigateToGitRef}/>,
                }} callout={{
                    type: "custom",
                    content: (<>
                                    <MigrateProjectVariablesBanner hasNoVariables={this.state.initialVariables?.length === 0}/>
                                    <ProjectGitHubAppAuthCheck project={this.state.project} permission={Permission.VariableEdit}/>
                                    <GitCallouts isVariablesPage gitRefResource={this.props.projectContext.state.gitRef}/>
                                </>),
                }}>
                        <FormContent>
                            {this.state.model && (<TransitionAnimation>
                                    <VariableEditor initialVariables={this.state.initialVariables!} scopeValues={this.state.variableSet!.ScopeValues} isTenanted={this.isProjectTenanted()} doBusyTask={this.doBusyTask} onVariablesChanged={this.onVariablesChanged} cellFocusResetKey={this.state.cellFocusResetKey} scope="Project" gitVariables={gitVariables} ephemeralEnvironmentsAreEnabled={this.props.ephemeralEnvironmentsAreEnabled}/>
                                    <VariableSaveConfirmationDialog key={"VariableEditorConfirmDialogs"} content={this.state.dialogContent} onClosed={() => this.setState({ dialogContent: null! })} onSaveClick={() => this.saveVariables(this.state.newBranch)}/>
                                </TransitionAnimation>)}
                        </FormContent>
                    </PageContent>)}
            </LegacyForm>);
    }
    private onVariablesChanged = (variables: any) => {
        this.setState({ model: { variables } });
        if (!this.props.gitVariableSplitChangeDetectionEnabled) {
            this.setState({ advancedChangeDetection: { isEnabled: false } });
            return;
        }
        const gitVariables = this.state.project ? HasVariablesInGit(this.state.project.PersistenceSettings) : false;
        if (!gitVariables) {
            // Advanced change detection is only used for Git variables (at the moment)
            this.setState({ advancedChangeDetection: { isEnabled: false } });
            return;
        }
        this.setState({
            advancedChangeDetection: {
                isEnabled: true,
                ...getVariableTypesChanged(variables, this.state.initialVariables!),
            },
        });
    };
    private getPrimaryAction = (gitVariables: boolean, createSaveAction: (options: PrimarySaveActionOptions) => PrimarySavePageAction): PrimaryPageAction => {
        if (gitVariables) {
            if (this.state.advancedChangeDetection.isEnabled) {
                const { sensitiveChanged, textChanged } = this.state.advancedChangeDetection;
                return getProjectVariablesPrimaryAction(sensitiveChanged, textChanged, createSaveAction, this.getCommitButtonPrimaryAction);
            }
            return this.getCommitButtonPrimaryAction(createSaveAction({ saveButtonLabel: "Commit", saveButtonBusyLabel: "Committing" }), false);
        }
        else {
            return createSaveAction({ saveButtonLabel: "Save", saveButtonBusyLabel: "Saving" });
        }
    };
    private getCommitButtonPrimaryAction = (defaultPrimaryAction: PrimarySavePageAction, sensitiveChanged: boolean): PrimaryPageAction => {
        return { type: "custom", content: <GetCommitButton {...this.getCommitButtonProps(defaultPrimaryAction, sensitiveChanged)}/>, key: "Get Commit" };
    };
    private isProjectTenanted() {
        return this.state.project ? this.state.project.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted : false;
    }
    private resetState(project: ProjectResource, hasSteps: boolean, variableSet: VariableSetResource, sensitiveVariableSet?: VariableSetResource) {
        const allVariables = [...variableSet.Variables];
        if (sensitiveVariableSet) {
            allVariables.push(...sensitiveVariableSet.Variables);
        }
        const groupedVariableResources = groupVariablesByName(allVariables, (v) => v.Name);
        const variables = createViewModel(groupedVariableResources);
        const model: SharedProjectVariablesPageInternalModel = { variables };
        this.setState({
            project,
            hasSteps,
            variableSet,
            sensitiveVariableSet,
            groupedVariableResources,
            initialVariables: [...variables],
            model,
            cleanModel: { ...model },
            cellFocusResetKey: DateFormatter.timestamp(),
            showOnboardingPage: variables.length === 0,
        });
        setTimeout(() => {
            if (allVariables.length <= 0) {
                this.props.onboardingContext?.variablePageLoadedWithNoDataLeft();
                return;
            }
            this.props.onboardingContext?.continueSampleProjectTour();
        }, 300);
    }
    async saveVariablesToNewBranch(branchName: string) {
        if (!this.state.project) {
            throw "Can not save if there is no project configured";
        }
        this.setState({ disableDirtyFormChecking: true });
        const newBranch = await repository.ProjectBranches.createBranch(this.props.projectContext.state.model.Id, branchName, this.props.projectContext.state.gitRef?.CanonicalName ?? "");
        await this.saveVariables(newBranch);
        this.props.navigateToGitRef(newBranch.Name);
        this.setState({ disableDirtyFormChecking: false });
    }
    async saveVariables(newBranch?: GitBranchResource) {
        const oldVariableCount = this.state.variableSet!.Variables.length;
        const variables = getVariableResources(this.state.model!.variables, this.state.groupedVariableResources!);
        const newVariableCount = variables.length;
        const actionMeta = oldVariableCount < newVariableCount ? "Added variables" : oldVariableCount === newVariableCount ? "Modified variables" : "Delete variables";
        const actionEvent: ActionEvent = {
            action: Action.Save,
            resource: "Variables",
            actionMeta: actionMeta,
        };
        const project = this.props.projectContext.state.model;
        if (HasVariablesInGit(project.PersistenceSettings)) {
            await this.saveVariablesGit(project, variables, actionEvent, newBranch);
        }
        else {
            await this.saveVariablesDatabase(project, variables, actionEvent);
        }
    }
    private async saveVariablesDatabase(project: ProjectResource, variables: VariableResource[], actionEvent: ActionEvent) {
        actionEvent.action = Action.Save;
        await this.props.trackAction("Save Project Variables", actionEvent, async () => {
            const repository = this.props.projectContext.state.projectContextRepository;
            const variableSet = await repository.Variables.modify({ ...this.state.variableSet!, Variables: variables });
            this.resetState(project, this.state.hasSteps, variableSet);
        });
    }
    private async saveVariablesGit(project: ProjectResource, variables: VariableResource[], actionEvent: ActionEvent, newBranch?: GitBranchResource) {
        actionEvent.action = Action.Commit;
        actionEvent.isDefaultBranch = this.props.projectContext.state.isDefaultBranch;
        actionEvent.commitMessage = this.state.commitMessage.summary.length > 0;
        await this.props.trackAction("Save Project Variables", actionEvent, async () => {
            const textVariables = variables.filter((variable) => variable.Type !== VariableType.Sensitive);
            const sensitiveVariables = variables.filter((variable) => variable.Type === VariableType.Sensitive);
            this.createNewIdForUpdatedToSensitive(sensitiveVariables);
            let variableSet = this.state.variableSet!;
            if (newBranch) {
                variableSet = {
                    ...variableSet,
                    Links: {
                        ...variableSet.Links,
                        Self: newBranch.Links.Variables,
                    },
                };
            }
            const repository = this.props.projectContext.state.projectContextRepository;
            const formattedCommitMessage = getFormattedCommitMessage(this.state.commitMessage, "Update variables");
            if (this.props.gitVariableSplitChangeDetectionEnabled) {
                const { sensitiveChanged, textChanged } = getVariableTypesChanged(this.state.model!.variables, this.state.initialVariables!);
                variableSet = textChanged ? await repository.Variables.modify({ ...variableSet, Variables: textVariables, ChangeDescription: formattedCommitMessage }) : variableSet;
                const sensitiveVariableSet = sensitiveChanged ? await repository.Variables.modify({ ...this.state.sensitiveVariableSet!, Variables: sensitiveVariables }) : this.state.sensitiveVariableSet!;
                this.resetState(project, this.state.hasSteps, variableSet, sensitiveVariableSet);
            }
            else {
                variableSet = await repository.Variables.modify({ ...variableSet, Variables: textVariables, ChangeDescription: formattedCommitMessage });
                const sensitiveVariableSet = await repository.Variables.modify({ ...this.state.sensitiveVariableSet!, Variables: sensitiveVariables });
                this.resetState(project, this.state.hasSteps, variableSet, sensitiveVariableSet);
            }
        });
    }
    private createNewIdForUpdatedToSensitive(sensitiveVariables: VariableResource[]) {
        // Create new ID for variables that have been changed to sensitive so that they can be saved to the server without conflicts.
        // See https://app.shortcut.com/octopusdeploy/story/75424/conflicts-with-cac-variables-and-sensitive-variables-requested-by-finnian-dempsey
        // and https://github.com/OctopusDeploy/Issues/issues/8733
        const initialVariableIdsAndType: Record<string, string> = {};
        this.state?.initialVariables?.forEach((variable) => {
            variable.values.forEach((value) => {
                initialVariableIdsAndType[value.Id] = value.Type;
            });
        });
        sensitiveVariables.forEach((variable) => {
            if (initialVariableIdsAndType[variable.Id] !== undefined && initialVariableIdsAndType[variable.Id] !== VariableType.Sensitive) {
                variable.Id = IdHelper.newId();
            }
        });
    }
}
function SharedProjectVariablesPage() {
    const projectContext = useProjectContext();
    const onboardingContext = useRequiredContext(SampleProjectTourContext);
    const trackAction = useProjectScopedAnalyticTrackedActionDispatch(projectContext.state.model.Id);
    const dispatchAction = useProjectScopedAnalyticActionDispatch(projectContext.state.model.Id);
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const gitVariableSplitChangeDetectionEnabled = useGitVariableSplitChangeDetectionEnabled();
    const spaceNavigation = useSpaceAwareNavigation();
    const { search: urlSearch } = useLocation();
    const { model: project, gitRef, gitRefValidationError } = projectContext.state;
    const navigateToGitRef = (gitRef: string) => {
        // Ideally, instead of concatenating the URL search property, it should use links.branchAllVariablesPage.generateUrl(routeParams, queryParams), and take queryParams from routing infrastructure.
        // We need to refactor the Advanced Filters in this page to achieve the ideal pattern.
        const newUrl = links.branchProjectVariablesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, branchName: gitRef }).resolveWithSpaceId(project.SpaceId);
        const fullUrl = `${newUrl}${urlSearch}`;
        spaceNavigation.navigate(fullUrl);
    };
    if (gitRefValidationError && gitRefValidationError.Type !== "Success") {
        return (<PageContent header={{ title: pageTitle }}>
                <VcsError project={project} error={gitRefValidationError} gitRef={gitRef?.CanonicalName} setGitRef={navigateToGitRef}/>
            </PageContent>);
    }
    return (<SharedProjectVariablesPageInternal onboardingContext={onboardingContext} projectContext={projectContext} trackAction={trackAction} dispatchAction={dispatchAction} gitVariableSplitChangeDetectionEnabled={gitVariableSplitChangeDetectionEnabled} ephemeralEnvironmentsAreEnabled={ephemeralEnvironmentsAreEnabled} navigateToGitRef={navigateToGitRef}/>);
}
export function useGitVariableSplitChangeDetectionEnabled() {
    return useOctopusFeatureToggle("git-variable-split-change-detection", false);
}
export default SharedProjectVariablesPage;
